import { useEffect, useRef } from "react";
import { LAND_MASK } from "../landMask";

const CONFIG = {
  bgNavy: "#050B1F",
  bgGlowCenter: "#092138",
  globeCyan: "#7af6ff",
  globeGreen: "#8dffc0",
  candlePalette: ["#ff942a", "#ffd65a", "#2ad6ce", "#39ff7a"],
  redOrangeNum: "#ff6a3d",
  whiteNum: "#d8e5f2",
  globeRadiusPercent: 0.38,
  globeCenterXPercent: 0.35,
  globeCenterYPercent: 0.50,
  rotationSpeed: 0.03,
  initialLongitudeOffset: -(20 * Math.PI / 180),
  dotDensityLat: 110,
  dotDensityLon: 220,
  reducedDensityFactor: 0.65,
  candleCount: 102,
  candleBodyWidth: 3.5,
  candleWickWidth: 1.2,
  loopDuration: 29.1,
  excludeTopRightWidthPercent: 0.35,
  excludeTopRightHeightPercent: 0.35,
  dofCandleCount: 6,
  floatingNumberCount: 40,
  dustParticleCount: 70,
  bokehOrbCount: 15,
  cameraDriftX: 7,
  cameraDriftY: 4,
};

function decodeMask(mask) {
  const binStr = atob(mask);
  const bytes = new Uint8Array(binStr.length);
  for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
  return bytes;
}

function landAt(maskBytes, lonDeg, latDeg) {
  const x = Math.floor(((lonDeg + 180) % 360 + 360) % 360);
  const y = Math.floor(Math.max(0, Math.min(179, 90 - latDeg)));
  const bitIndex = y * 360 + x;
  return (maskBytes[bitIndex >> 3] & (1 << (7 - (bitIndex & 7)))) !== 0;
}

function paletteColor(t) {
  const palette = CONFIG.candlePalette;
  const s = t * (palette.length - 1);
  const idx = Math.floor(s);
  const rem = s - idx;
  if (idx >= palette.length - 1) return palette[palette.length - 1];
  const hex = (h) => {
    const n = parseInt(h.replace("#", ""), 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };
  const c1 = hex(palette[idx]);
  const c2 = hex(palette[idx + 1]);
  return `rgb(${Math.round(c1.r + (c2.r - c1.r) * rem)}, ${Math.round(c1.g + (c2.g - c1.g) * rem)}, ${Math.round(c1.b + (c2.b - c1.b) * rem)})`;
}

export default function HeroBackdrop() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const maskBytes = decodeMask(LAND_MASK);
    let width = 0;
    let height = 0;
    let dpr = 1;
    let running = true;
    let raf = 0;
    let lastTime = performance.now();
    let totalTime = 0;
    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let prefersReducedMotion = reducedQuery.matches;
    let mobile = false;
    let globeBasis = 0;
    const sceneH = () => (mobile && globeBasis ? globeBasis : height);
    let scrolling = false;
    let scrollTimer = 0;

    const globeDots = [];
    const buildGlobeDots = (isMobile) => {
      globeDots.length = 0;
      const latSteps = isMobile ? Math.floor(CONFIG.dotDensityLat * 0.38) : CONFIG.dotDensityLat;
      const lonSteps = isMobile ? Math.floor(CONFIG.dotDensityLon * 0.38) : CONFIG.dotDensityLon;
      for (let i = 0; i <= latSteps; i++) {
        const lat = -85 + (170 * i) / latSteps;
        const phi = (lat * Math.PI) / 180;
        const cosPhi = Math.cos(phi);
        const sinPhi = Math.sin(phi);
        const circumPoints = Math.max(8, Math.floor(lonSteps * cosPhi));
        for (let j = 0; j < circumPoints; j++) {
          const lon = -180 + (360 * j) / circumPoints;
          const theta = (lon * Math.PI) / 180;
          globeDots.push({
            x0: cosPhi * Math.sin(theta),
            y0: -sinPhi,
            z0: cosPhi * Math.cos(theta),
            lon: theta,
            lat: phi,
            isLand: landAt(maskBytes, lon, lat),
          });
        }
      }
    };

    const candles = [];
    const generateCandles = () => {
      candles.length = 0;
      const n = CONFIG.candleCount;
      for (let i = 0; i < n; i++) {
        const t = i / (n - 1);
        const jagged = Math.sin(i * 1.8) * 0.08 + Math.cos(i * 0.9) * 0.04;
        const baseline = 0.14 + t * 0.66;
        const val = Math.max(0.1, Math.min(0.82, baseline + jagged));
        const isUp = i % 4 !== 1;
        const bodySize = 0.04 + Math.abs(Math.sin(i * 2.1)) * 0.05;
        const open = isUp ? val - bodySize * 0.5 : val + bodySize * 0.5;
        const close = isUp ? val + bodySize * 0.5 : val - bodySize * 0.5;
        candles.push({
          xRel: t,
          open,
          close,
          high: Math.max(open, close) + 0.025 + Math.abs(Math.cos(i * 1.1)) * 0.035,
          low: Math.min(open, close) - (0.02 + Math.abs(Math.sin(i * 1.5)) * 0.03),
          colorT: t,
        });
      }
    };

    const floatingNumbers = [];
    const dustParticles = [];
    const bokehOrbs = [];
    for (let i = 0; i < CONFIG.floatingNumberCount; i++) {
      floatingNumbers.push({
        x: Math.random(),
        y: Math.random(),
        val: (Math.random() > 0.4 ? "+" : "-") + (Math.random() * 85 + 10).toFixed(Math.random() > 0.5 ? 2 : 4),
        color: Math.random() > 0.4 ? CONFIG.redOrangeNum : CONFIG.whiteNum,
        alpha: 0.05 + Math.random() * 0.06,
        speedY: -(0.006 + Math.random() * 0.012),
      });
    }
    for (let i = 0; i < CONFIG.dustParticleCount; i++) {
      dustParticles.push({
        x: Math.random(),
        y: Math.random(),
        r: 0.6 + Math.random() * 1.2,
        alpha: 0.08 + Math.random() * 0.12,
        color: Math.random() > 0.5 ? "#ffd65a" : "#7af6ff",
        vx: (Math.random() - 0.5) * 0.005,
        vy: -(0.003 + Math.random() * 0.009),
      });
    }
    for (let i = 0; i < CONFIG.bokehOrbCount; i++) {
      bokehOrbs.push({
        x: Math.random(),
        y: Math.random(),
        r: 18 + Math.random() * 45,
        alpha: 0.015 + Math.random() * 0.02,
        isCyan: Math.random() > 0.5,
        vx: (Math.random() - 0.5) * 0.002,
        vy: (Math.random() - 0.5) * 0.002,
      });
    }

    const excluded = (xRel, yRel) =>
      xRel >= 1 - CONFIG.excludeTopRightWidthPercent && yRel <= CONFIG.excludeTopRightHeightPercent;

    const drawDof = () => {
      const startX = width * 0.82;
      const count = CONFIG.dofCandleCount;
      const spacing = (width * 0.16) / count;
      for (let i = 0; i < count; i++) {
        const cx = startX + i * spacing;
        const cy = sceneH() * 0.45 + Math.sin(i * 1.5) * (sceneH() * 0.14);
        const candleH = sceneH() * (0.18 + Math.abs(Math.cos(i * 0.8)) * 0.12);
        const candleW = 12;
        const wickGrad = ctx.createRadialGradient(cx, cy, 2, cx, cy, candleH * 0.65);
        wickGrad.addColorStop(0, "rgba(57, 255, 122, 0.08)");
        wickGrad.addColorStop(0.8, "rgba(57, 255, 122, 0.02)");
        wickGrad.addColorStop(1, "rgba(57, 255, 122, 0)");
        ctx.fillStyle = wickGrad;
        ctx.fillRect(cx - 3, cy - candleH * 0.7, 6, candleH * 1.4);
        const bodyGrad = ctx.createRadialGradient(cx, cy, 3, cx, cy, candleW * 1.6);
        bodyGrad.addColorStop(0, "rgba(57, 255, 122, 0.07)");
        bodyGrad.addColorStop(0.5, "rgba(57, 255, 122, 0.025)");
        bodyGrad.addColorStop(1, "rgba(57, 255, 122, 0)");
        ctx.fillStyle = bodyGrad;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(cx - candleW * 1.2, cy - candleH * 0.5, candleW * 2.4, candleH, 10);
        else ctx.rect(cx - candleW * 1.2, cy - candleH * 0.5, candleW * 2.4, candleH);
        ctx.fill();
      }
    };

    const drawCandles = (loopT) => {
      const n = CONFIG.candleCount;
      const windowSize = 36;
      const fadeSpan = 12;
      const head = (loopT / CONFIG.loopDuration) * n;
      const chartBaselineY = sceneH() * 0.9;
      const chartHeight = chartBaselineY - sceneH() * 0.16;
      const trendPoints = [];
      for (let i = 0; i < n; i++) {
        let behind = head - i;
        behind = ((behind % n) + n) % n;
        if (behind > windowSize + fadeSpan) continue;
        const growth = behind < 1 ? behind : 1;
        const life = behind > windowSize ? 1 - (behind - windowSize) / fadeSpan : 1;
        const easeGrowth = 1 - Math.pow(1 - Math.max(0, Math.min(1, growth)), 3);
        const candle = candles[i];
        const cx = candle.xRel * width;
        const openY = chartBaselineY - candle.open * chartHeight;
        const closeY = chartBaselineY - candle.close * chartHeight;
        const highY = chartBaselineY - candle.high * chartHeight;
        const lowY = chartBaselineY - candle.low * chartHeight;
        const curHighY = lowY + (highY - lowY) * easeGrowth;
        const curOpenY = lowY + (openY - lowY) * easeGrowth;
        const curCloseY = lowY + (closeY - lowY) * easeGrowth;
        const bodyTop = Math.min(curOpenY, curCloseY);
        const bodyBottom = Math.max(curOpenY, curCloseY);
        const colorStr = paletteColor(candle.colorT);
        ctx.save();
        ctx.globalAlpha = 0.22 * life * Math.min(1, easeGrowth * 1.4);
        ctx.strokeStyle = colorStr;
        ctx.lineWidth = CONFIG.candleWickWidth;
        ctx.beginPath();
        ctx.moveTo(cx, lowY);
        ctx.lineTo(cx, curHighY);
        ctx.stroke();
        ctx.fillStyle = colorStr;
        ctx.fillRect(cx - CONFIG.candleBodyWidth * 0.5, bodyTop, CONFIG.candleBodyWidth, Math.max(2.5, bodyBottom - bodyTop));
        ctx.restore();
        if (easeGrowth > 0.75 && life > 0.35) trendPoints.push({ x: cx, y: highY });
      }
      if (trendPoints.length > 2) {
        trendPoints.sort((a, b) => a.x - b.x);
        const gap = (width / n) * 3;
        ctx.save();
        ctx.strokeStyle = "rgba(255, 214, 90, 0.22)";
        ctx.lineWidth = 1;
        ctx.globalAlpha = 0.28;
        ctx.beginPath();
        ctx.moveTo(trendPoints[0].x, trendPoints[0].y);
        for (let j = 1; j < trendPoints.length; j++) {
          if (trendPoints[j].x - trendPoints[j - 1].x > gap) {
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(trendPoints[j].x, trendPoints[j].y);
          } else {
            ctx.lineTo(trendPoints[j].x, trendPoints[j].y);
          }
        }
        ctx.stroke();
        ctx.restore();
      }
    };

    const drawFrame = (loopT, absTime) => {
      const camX = mobile ? 0 : Math.sin(absTime * 0.35) * CONFIG.cameraDriftX;
      const camY = mobile ? 0 : Math.cos(absTime * 0.28) * CONFIG.cameraDriftY;
      ctx.save();
      ctx.translate(camX, camY);
      ctx.fillStyle = CONFIG.bgNavy;
      ctx.fillRect(-20, -20, width + 40, height + 40);
      const globeX = width * CONFIG.globeCenterXPercent;
      const globeY = sceneH() * CONFIG.globeCenterYPercent;
      const globeRadius = sceneH() * CONFIG.globeRadiusPercent;
      const bgGlow = ctx.createRadialGradient(globeX, globeY, globeRadius * 0.2, globeX, globeY, globeRadius * 2.3);
      bgGlow.addColorStop(0, CONFIG.bgGlowCenter);
      bgGlow.addColorStop(0.5, "#07152b");
      bgGlow.addColorStop(1, CONFIG.bgNavy);
      ctx.fillStyle = bgGlow;
      ctx.fillRect(-20, -20, width + 40, height + 40);

      if (!mobile) for (let i = 0; i < bokehOrbs.length; i++) {
        const b = bokehOrbs[i];
        if (!prefersReducedMotion) {
          b.x = (b.x + b.vx * 0.3 + 1) % 1;
          b.y = (b.y + b.vy * 0.3 + 1) % 1;
        }
        if (excluded(b.x, b.y)) continue;
        const bx = b.x * width;
        const by = b.y * height;
        const rgb = b.isCyan ? "122, 246, 255" : "255, 214, 90";
        const radGrad = ctx.createRadialGradient(bx, by, 0, bx, by, b.r);
        radGrad.addColorStop(0, `rgba(${rgb}, ${b.alpha})`);
        radGrad.addColorStop(0.6, `rgba(${rgb}, ${b.alpha * 0.35})`);
        radGrad.addColorStop(1, `rgba(${rgb}, 0)`);
        ctx.fillStyle = radGrad;
        ctx.beginPath();
        ctx.arc(bx, by, b.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.save();
      ctx.font = "10px monospace";
      if (!mobile) for (let i = 0; i < floatingNumbers.length; i++) {
        const fn = floatingNumbers[i];
        if (!prefersReducedMotion) {
          fn.y += fn.speedY * 0.08;
          if (fn.y < -0.05) {
            fn.y = 1.05;
            fn.x = Math.random();
          }
        }
        if (excluded(fn.x, fn.y)) continue;
        ctx.fillStyle = fn.color;
        ctx.globalAlpha = fn.alpha;
        ctx.fillText(fn.val, fn.x * width, fn.y * height);
      }
      ctx.restore();

      const haloGrad = ctx.createRadialGradient(globeX, globeY, globeRadius * 0.88, globeX, globeY, globeRadius * 1.15);
      haloGrad.addColorStop(0, "rgba(122, 246, 255, 0.16)");
      haloGrad.addColorStop(0.3, "rgba(122, 246, 255, 0.06)");
      haloGrad.addColorStop(0.7, "rgba(42, 214, 206, 0.02)");
      haloGrad.addColorStop(1, "rgba(5, 11, 31, 0)");
      ctx.fillStyle = haloGrad;
      ctx.beginPath();
      ctx.arc(globeX, globeY, globeRadius * 1.16, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(globeX, globeY, globeRadius * 1.008, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(122, 246, 255, 0.22)";
      ctx.lineWidth = 1.2;
      ctx.stroke();

      const rotAngle = CONFIG.initialLongitudeOffset + absTime * CONFIG.rotationSpeed;
      const cosRot = Math.cos(rotAngle);
      const sinRot = Math.sin(rotAngle);
      for (let i = 0; i < globeDots.length; i++) {
        const dot = globeDots[i];
        const rx = dot.x0 * cosRot + dot.z0 * sinRot;
        const ry = dot.y0;
        const rz = -dot.x0 * sinRot + dot.z0 * cosRot;
        if (rz <= 0.015) continue;
        const px = globeX + rx * globeRadius;
        const py = globeY + ry * globeRadius;
        if (dot.isLand) {
          ctx.fillStyle = (dot.lon + dot.lat) % 0.3 > 0.14 ? CONFIG.globeGreen : CONFIG.globeCyan;
          ctx.globalAlpha = Math.min(0.26, 0.07 + rz * 0.14);
          ctx.beginPath();
          ctx.arc(px, py, 1.05 + rz * 0.7, 0, Math.PI * 2);
          ctx.fill();
        } else if (!mobile && i % 3 === 0) {
          ctx.fillStyle = "#3a7da8";
          ctx.globalAlpha = 0.02 * rz;
          ctx.beginPath();
          ctx.arc(px, py, 0.6, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      if (!mobile) drawDof();
      drawCandles(loopT);

      if (!mobile) for (let i = 0; i < dustParticles.length; i++) {
        const d = dustParticles[i];
        if (!prefersReducedMotion) {
          d.x = (d.x + d.vx * 0.2 + 1) % 1;
          d.y = (d.y + d.vy * 0.2 + 1) % 1;
        }
        if (excluded(d.x, d.y)) continue;
        ctx.beginPath();
        ctx.arc(d.x * width, d.y * height, d.r, 0, Math.PI * 2);
        ctx.fillStyle = d.color;
        ctx.globalAlpha = d.alpha;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    };

    const fit = () => {
      const box = canvas.parentElement || canvas;
      const nextW = box.clientWidth || window.innerWidth;
      const nextH = box.clientHeight || window.innerHeight;
      if (nextW === width && nextH === height) return;
      const widthChanged = !width || Math.abs(nextW - width) > 48;
      if (widthChanged || !globeBasis) globeBasis = nextH;
      width = nextW;
      height = nextH;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      mobile = width < 768;
      dpr = mobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      buildGlobeDots(mobile);
      generateCandles();
      drawFrame(prefersReducedMotion ? 4.5 : totalTime % CONFIG.loopDuration, totalTime);
    };

    const renderLoop = (now) => {
      if (!running) return;
      if (document.hidden || scrolling) return;
      const delta = (now - lastTime) / 1000;
      lastTime = now;
      totalTime += Math.min(delta, 0.1);
      const effectiveTime = prefersReducedMotion ? 4.5 : totalTime;
      drawFrame(effectiveTime % CONFIG.loopDuration, effectiveTime);
      if (!prefersReducedMotion) raf = requestAnimationFrame(renderLoop);
    };

    const onVisibility = () => {
      if (!document.hidden && running && !prefersReducedMotion) {
        lastTime = performance.now();
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(renderLoop);
      }
    };
    const onMotion = (e) => {
      prefersReducedMotion = e.matches;
      if (prefersReducedMotion) {
        cancelAnimationFrame(raf);
        drawFrame(4.5, 4.5);
      } else if (running) {
        lastTime = performance.now();
        raf = requestAnimationFrame(renderLoop);
      }
    };

    const onScroll = () => {
      if (!mobile || prefersReducedMotion) return;
      scrolling = true;
      cancelAnimationFrame(raf);
      clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        scrolling = false;
        if (!running || document.hidden) return;
        lastTime = performance.now();
        raf = requestAnimationFrame(renderLoop);
      }, 140);
    };

    fit();
    if (!prefersReducedMotion) raf = requestAnimationFrame(renderLoop);
    window.addEventListener("resize", fit, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);
    reducedQuery.addEventListener("change", onMotion);
    return () => {
      running = false;
      clearTimeout(scrollTimer);
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
      reducedQuery.removeEventListener("change", onMotion);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ display: "block", width: "100%", height: "100%" }}
    />
  );
}
